import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Grid,
  Tooltip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { ConstantActions, ManagementActions } from '../../../Services/_actions/index.js';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import Pagination from '../../FlowBuilder/Common/Pagination/index.tsx';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import {
  OrganisationFormData,
  PaginatedResponse,
} from '../../../interfaces/Management.interface.ts';
import {
  organisationFormDataAtom,
  organisationTableStateAtom,
} from '../../../Services/_states/index.js';
import {
  useRecoilState,
  useRecoilValueLoadable,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import { useNavigate } from 'react-router';
import useDebounce from '../../Shared/Delay/index.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import OrganisationModal from '../User/OrganisationDetailModal.tsx';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import { fetchOrganisationsSelector } from '../../../Services/_states/Organisation/organisationSelector.ts';
import { generateFilterKey } from '../../../utils/helper.jsx';
import LoadingDisableWrapper from '../../Shared/LoadingDisableWrapper/index.tsx';
import OrganisationCreationHelp from '../../Shared/HelpFIles/OrganisationCreationHelp.tsx';
import { globalpermissions } from '../../../Services/Permission.js';

const List: React.FC = () => {
  const constantActions = ConstantActions();
  const managementActions = ManagementActions();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const setFormData = useSetRecoilState(organisationFormDataAtom);
  const resetFormData = useResetRecoilState(organisationFormDataAtom);

  const [organisationRes, setOrganisationRes] = useState<PaginatedResponse>({
    list: [],
    pages: 0,
    count: 0,
  });
  const [organisationFilter, setOrganisationFilter] = useState({
    page: 0,
    search: '',
    category: '',
    limit: 20,
  });
  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);
  // const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // const [confirmOpen, setConfirmOpen] = useState(false);
  // const [organisationToDelete, setOrganisationToDelete] = useState<OrganisationFormData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [tableState, setTableState] = useRecoilState(organisationTableStateAtom);
  const fetchedData = useRecoilValueLoadable(fetchOrganisationsSelector);
  const [searchInput, setSearchInput] = useState(tableState?.filters?.search || '');
  const debouncedSearch = useDebounce(searchInput, 500);

  const handleView = (organisation: any) => {
    setSelectedOrganisation(organisation);
    setModalOpen(true);
  };
  const handleClickEdit = (organisation: OrganisationFormData) => {
    setFormData(organisation);
    navigate('/r/management/organisation/edit/');
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOrganisation(null);
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'index',
        header: 'S.No.',
        textAlign: 'center',
        enableSorting: false,
        size: 50,
        Cell: ({ row }) => {
          const page = tableState?.filters?.page || 0;
          const limit = tableState?.filters?.limit || 20;
          return page * limit + row.index + 1;
        },
      },
      {
        accessorKey: 'name',
        header: 'Organisation Name',
        textAlign: 'center',
        enableSorting: false,
        size: 300,
      },
      {
        accessorKey: 'category',
        header: 'Category',
        textAlign: 'center',
        enableSorting: false,
        size: 300,
        Cell: ({ row }) => row.original?.category || 'N/A',
      },
      {
        accessorKey: 'action',
        header: 'Actions',
        textAlign: 'center',
        enableSorting: false,
        size: 30,
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
          const open = Boolean(anchorEl);

          const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          return (
            <>
              <IconButton onClick={handleClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleView(row.original);
                  }}
                >
                  View
                </MenuItem>
                {constantActions.userHasPermission([
                  globalpermissions?.managementRoutes?.editOrganisation,
                ]) && (
                    <MenuItem onClick={() => handleClickEdit(row?.original)}>Edit</MenuItem>
                  )}
                {/* <MenuItem
                  onClick={() => {
                    handleDelete(row.original);
                    handleClose();
                  }}
                >
                  Delete
                </MenuItem> */}
              </Menu>
            </>
          );
        },
      },
    ],
    [tableState?.filters]
  );

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => (window.location.href = '/'),
    },
    { label: 'Organisations' },
  ];

  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      setTableState((prev) => ({
        ...prev,
        cache: { ...prev.cache, [filterKey]: fetchedData.contents.data },
        totalCountCache: { ...prev.totalCountCache, [filterKey]: fetchedData.contents.totalCount },
      }));
    }
  }, [fetchedData, setTableState]);

  const handleChangePage = (event, newPage) => {
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, page: newPage },
    }));
  };
  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 20) || 20;
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, limit: value },
    }));
  };

  const handleSearch = (query) => {
    const newFilters = {
      ...tableState?.filters,
      page: 0,
      search: query,
    };
    setTableState((prev) => ({
      ...prev,
      filters: { ...newFilters },
    }));
  };

  useEffect(() => {
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, page: 0, search: debouncedSearch },
    }));
  }, [debouncedSearch, setTableState]);

  return (
    <Box
      sx={{
        padding: '5px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Organisations
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
          {constantActions.userHasPermission([
            globalpermissions?.managementRoutes?.createOrganisation,
          ]) && (
              <Tooltip title="Add New Organisation">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                    resetFormData();
                    navigate('/r/management/organisation/create/');
                  }}
                >
                  Add
                </Button>
              </Tooltip>
            )}
          <OrganisationCreationHelp />
        </Grid>
      </Grid>
      <Grid
        container
        sx={{ alignItems: 'center', paddingTop: '10px' }}
        className="search-box-style"
      >
        <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center', pb: 2.5 }}>
          <LoadingDisableWrapper loading={fetchedData.state === 'loading'}>
            <SearchBox handleSearch={(query) => setSearchInput(query)} value={searchInput} disabled={fetchedData.state === 'loading'} />
          </LoadingDisableWrapper>
        </Grid>
      </Grid>

      <CommonTable
        columns={columns}
        data={tableState?.cache[generateFilterKey(tableState?.filters)] || []}
        // data={organisationRes?.list || []}
        muiTableBodyCellProps={{
          sx: (theme) => ({
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
          }),
        }}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 317px)', width: '100%' } }}
        muiTableHeadCellProps={{
          sx: {
            backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            fontFamily: 'system-ui',
          },
        }}
        state={{
          showColumnFilters: false,
          isLoading: fetchedData.state === 'loading',
        }}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableStickyHeader={true}
        enableTopToolbar={false}
        initialState={{
          density: 'compact',
        }}
        enablePagination={false}
        manualPagination={false}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
            <Pagination
              totalCount={tableState?.totalCountCache[generateFilterKey(tableState?.filters)] || 0}
              rowsPerPage={tableState?.filters?.limit}
              page={tableState?.filters?.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      <OrganisationModal
        open={modalOpen}
        onClose={handleCloseModal}
        organisation={selectedOrganisation}
      />
    </Box>
  );
};

export default List;
